import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  OnAddTemplatesToFolderRequest,
  Template,
  TemplatesResult,
} from "../interfaces/templates.types.ts";
import { setTemplateTagColors } from "../slices/templatesSlice.ts";
import { Folder } from "../interfaces/folder.types.ts";
import api from "../../api";
import { generateTagColors } from "../../utils/common/generateTagColors.ts";
import { getExceptionMessage } from "../../exceptions/helpers/getExceptionMessage.ts";
import { templatesExceptions } from "../../exceptions/default/templatesExceptions.ts";

export const fetchAllTemplates = createAsyncThunk(
  "templates/fetchAllTemplates",
  async (url: string, thunkAPI) => {
    try {
      const response = await api.get<TemplatesResult>(url);

      const tagColors = generateTagColors(
        response.data.results.map((template) => template.name),
      );
      thunkAPI.dispatch(setTemplateTagColors(tagColors));

      return response.data;
    } catch (error: any) {
      const msg = getExceptionMessage(
        error.response.data?.code,
        templatesExceptions.fetchAllTemplates,
      );
      return thunkAPI.rejectWithValue(msg);
    }
  },
);

export const getTemplatesNotInFolder = createAsyncThunk(
  "templates/getTemplatesNotInFolder",
  async (folder: Folder, thunkAPI) => {
    try {
      const templates = await thunkAPI
        .dispatch(fetchAllTemplates(`/docvault/templates/`))
        .unwrap();

      const folderTemplates =
        folder.subfolders?.map(({ template }) => template.id) ||
        folder.templates?.map(({ id }) => id) ||
        [];
      const newTemplates = templates.results.filter(
        (template) => !folderTemplates.includes(template.id),
      );

      return newTemplates;
    } catch (error: any) {
      const msg = getExceptionMessage(
        error.response.data?.code,
        templatesExceptions.getTemplatesNotInFolder,
      );
      return thunkAPI.rejectWithValue(msg);
    }
  },
);

export const addTemplatesToFolder = createAsyncThunk(
  "templates/addTemplatesToFolder",
  async (
    {
      folderId,
      templates,
      guarantor_email,
      landlord_email,
    }: OnAddTemplatesToFolderRequest,
    thunkAPI,
  ) => {
    try {
      const response = await api.post<Folder>(
        `/docvault/folders/${folderId}/templates/`,
        {
          templates,
          guarantor_email,
          landlord_email,
        },
      );
      return response.data;
    } catch (error: any) {
      const msg = getExceptionMessage(
        error.response.data?.code,
        templatesExceptions.addTemplatesToFolder,
      );
      return thunkAPI.rejectWithValue(msg);
    }
  },
);

export const addSubfolderToFolder = createAsyncThunk(
  "templates/addSubfolderToFolder",
  async (
    {
      folderId,
      template_slug,
      metadata,
    }: { folderId: string; template_slug: string; metadata?: Record<string, any> },
    thunkAPI,
  ) => {
    try {
      // New endpoint (MB-430): creates a fresh subfolder, allowing several of the
      // same role (one per participant) — unlike the templates M2M endpoint.
      // NOTE: this returns the created SUBFOLDER (not the Folder).
      const response = await api.post(
        `/docvault/folders/${folderId}/subfolders/`,
        {
          template_slug,
          metadata: metadata || {},
        },
      );
      return response.data;
    } catch (error: any) {
      const msg = getExceptionMessage(
        error.response?.data?.code,
        "No se pudo agregar el participante.",
      );
      return thunkAPI.rejectWithValue(msg);
    }
  },
);

export const removeSubfolderFromFolder = createAsyncThunk(
  "templates/removeSubfolderFromFolder",
  async (
    { folderId, subfolderId }: { folderId: string; subfolderId: string },
    thunkAPI,
  ) => {
    try {
      // MB-431/MB-435: delete a single subfolder by id. Deleting by template slug
      // would drop EVERY participant of that role (all duplicates), so we target
      // the specific instance instead.
      await api.delete(`/docvault/folders/${folderId}/subfolders/${subfolderId}/`);
      return subfolderId;
    } catch (error: any) {
      const msg = getExceptionMessage(
        error.response?.data?.code,
        "No se pudo eliminar el participante.",
      );
      return thunkAPI.rejectWithValue(msg);
    }
  },
);

export const fetchTemplateByID = createAsyncThunk(
  "templates/fetchTemplateByID",
  async (id: string, thunkAPI) => {
    try {
      const response = await api.get<Template>(`/docvault/templates/${id}`);
      return response.data;
    } catch (error: any) {
      const msg = getExceptionMessage(
        error.response.data?.code,
        templatesExceptions.fetchTemplateByID,
      );
      return thunkAPI.rejectWithValue(msg);
    }
  },
);
